import React, { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Globe, Shield, Save, Users, BookOpen } from 'lucide-react';

interface WebsiteConfig {
  totalUsers: string;
  totalCourses: string;
}

export default function AdminWeb() {
  const [config, setConfig] = useState<WebsiteConfig>({
    totalUsers: '2,000+',
    totalCourses: '34+',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const handleInputChange = (field: keyof WebsiteConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // TODO: Implement API call to save configuration
      // await websiteConfigService.updateConfig(config);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveMessage('Configuration saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveMessage('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <section className="relative py-12 px-6 overflow-hidden min-h-screen">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
              <Shield className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400">Admin Access</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Website Configuration
              </span>
            </h1>
            <p className="text-xl text-gray-400">
              Update website statistics and displayed information
            </p>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`mb-6 p-4 rounded-lg border ${
              saveMessage.includes('success')
                ? 'bg-green-500/20 border-green-500/30 text-green-400'
                : 'bg-red-500/20 border-red-500/30 text-red-400'
            }`}>
              {saveMessage}
            </div>
          )}

          {/* Configuration Form */}
          <form onSubmit={handleSave}>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <div className="flex items-center space-x-3 mb-8">
                <Globe className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold">Homepage Statistics</h2>
              </div>

              <div className="space-y-6">
                {/* Total Users Field */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    <span>Total Users</span>
                  </label>
                  <input
                    type="text"
                    value={config.totalUsers}
                    onChange={(e) => handleInputChange('totalUsers', e.target.value)}
                    placeholder="e.g., 10,000+"
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    This value will be displayed on the homepage statistics section
                  </p>
                </div>

                {/* Total Courses Field */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
                    <BookOpen className="w-4 h-4 text-purple-400" />
                    <span>Total Courses</span>
                  </label>
                  <input
                    type="text"
                    value={config.totalCourses}
                    onChange={(e) => handleInputChange('totalCourses', e.target.value)}
                    placeholder="e.g., 34+"
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    This value will be displayed on the homepage statistics section
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
                </button>
              </div>
            </div>
          </form>

          {/* Info Box */}
          <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-400 mb-2">Note</h3>
            <p className="text-sm text-gray-400">
              Changes made here will update the statistics displayed on your website's homepage. 
              Make sure to review the changes before saving. Backend integration for these settings 
              will be implemented in a future update.
            </p>
          </div>
        </div>
      </section>
    </AdminLayout>
  );
}
